import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Building2, Megaphone, ShieldAlert, Star, Users, Wallet } from "lucide-react";
import { AdminCard, AdminPageHeader, StatCard } from "@/components/admin/admin-shell";
import { adminList, fetchAllListings, fetchAllUsers } from "@/lib/admin";
import { formatPrice } from "@/lib/data";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const listings = useQuery({ queryKey: ["admin-listings"], queryFn: fetchAllListings });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: fetchAllUsers });
  const stores = useQuery({ queryKey: ["admin", "stores"], queryFn: () => adminList("stores") });
  const tx = useQuery({ queryKey: ["admin", "transactions"], queryFn: () => adminList("transactions") });
  const reports = useQuery({ queryKey: ["admin", "reports"], queryFn: () => adminList("reports") });
  const reviews = useQuery({ queryKey: ["admin", "reviews"], queryFn: () => adminList("reviews") });

  const all = listings.data ?? [];
  const pending = all.filter((l) => l.status === "pending" || l.status === "draft");
  const vip = all.filter((l) => l.badge === "vip").length;
  const premium = all.filter((l) => l.badge === "premium").length;
  const income = (tx.data ?? [])
    .filter((t) => t["status"] === "completed" && Number(t["amount"]) > 0)
    .reduce((s, t) => s + Number(t["amount"] ?? 0), 0);
  const openReports = (reports.data ?? []).filter((r) => r["status"] === "open").length;
  const pendingReviews = (reviews.data ?? []).filter((r) => r["status"] === "pending").length;
  const totalViews = all.reduce((s, l) => s + (l.views ?? 0), 0);

  const byCategory = new Map<string, number>();
  const byCity = new Map<string, number>();
  for (const l of all) {
    byCategory.set(l.category, (byCategory.get(l.category) ?? 0) + 1);
    byCity.set(l.city, (byCity.get(l.city) ?? 0) + 1);
  }
  const top = (m: Map<string, number>) =>
    [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxCat = Math.max(1, ...byCategory.values());

  return (
    <div>
      <AdminPageHeader
        title="İdarə paneli"
        description="Platformanın ümumi göstəriciləri və statistikası"
      />

      <div className="grid grid-cols-2 gap-3">
        <StatCard className="p-3 text-2xl" label="Ümumi elanlar" value={String(all.length)} icon={Megaphone} hint={`${pending.length} moderasiya gözləyir`} />
        <StatCard className="p-3 text-2xl" label="İstifadəçilər" value={String((users.data ?? []).length)} icon={Users} />
        <StatCard className="p-3 text-2xl" label="Mağazalar" value={String((stores.data ?? []).length)} icon={Building2} />
        <StatCard className="p-3 text-2xl" label="Ümumi gəlir" value={formatPrice(income)} icon={Wallet} hint="Təsdiqlənmiş ödənişlər" />
        <StatCard className="p-3 text-2xl" label="VIP elanlar" value={String(vip)} icon={Star} />
        <StatCard className="p-3 text-2xl" label="Premium elanlar" value={String(premium)} icon={Star} />
        <StatCard className="p-3 text-2xl" label="Açıq şikayətlər" value={String(openReports)} icon={ShieldAlert} />
        <StatCard className="p-3 text-2xl" label="Ümumi baxış" value={String(totalViews)} icon={BarChart3} hint={`${pendingReviews} rəy təsdiq gözləyir`} />
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        <AdminCard className="p-5">
          <h2 className="mb-4 text-lg font-bold">Kateqoriyalar üzrə elanlar</h2>
          <div className="space-y-3">
            {top(byCategory).map(([name, count]) => (
              <div key={name}>
                <div className="mb-1 flex justify-between text-sm">
                  <span className="capitalize">{name}</span>
                  <span className="font-semibold">{count}</span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{ width: `${(count / maxCat) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {byCategory.size === 0 && <p className="text-sm text-muted-foreground">Məlumat yoxdur.</p>}
          </div>
        </AdminCard>

        <AdminCard className="p-5">
          <h2 className="mb-4 text-lg font-bold">Şəhərlər üzrə elanlar</h2>
          <ul className="space-y-2 text-sm">
            {top(byCity).map(([name, count]) => (
              <li key={name} className="flex justify-between rounded-2xl bg-muted/50 px-4 py-2">
                <span>{name}</span>
                <span className="font-semibold">{count}</span>
              </li>
            ))}
            {byCity.size === 0 && <p className="text-muted-foreground">Məlumat yoxdur.</p>}
          </ul>
        </AdminCard>
      </div>
    </div>
  );
}
